import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { PublicationBookingModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import type { SendUnapprovedArtworkMessagePayload } from '@hooks/usePublicationBooking'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/publicationBooking/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/publicationBooking/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/publicationBooking/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/publicationBooking/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/publicationBooking/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/publicationBooking/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/publicationBooking/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/publicationBooking/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/publicationBooking/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/publicationBooking/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/publicationBooking/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/publicationBooking/DELETE_FAILURE'

export type PublicationBookingModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PublicationBookingModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPublicationBookings(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().publicationBookings.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_bookings.json', options)
      .then(({ data }: { data: PublicationBookingModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPublicationBooking(publicationBookingId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.publicationBookings.loadedForKeys]
    const loadedIds = [...state.publicationBookings.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (publicationBookingId && !loadedIds.includes(publicationBookingId)){
      loadedIds.push(publicationBookingId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/publication_bookings/${publicationBookingId}.json`, options)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function checkPublicationBookingArtworks(publicationBookingId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/publication_bookings/${publicationBookingId}/check_artworks.json`, options)
      .then(({ data }: { data: { id: number, ready: boolean} }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicationBooking(
  publicationBooking: Partial<PublicationBookingModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_booking: publicationBooking,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_bookings.json', options, config)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePublicationBooking(
  publicationBooking: UpdateParams<PublicationBookingModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_booking: publicationBooking,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}.json`, options, config)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePublicationBookingPreflightStatus(
  publicationBooking: UpdateParams<PublicationBookingModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_booking: publicationBooking,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}/update_preflight_status.json`, options, config)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function approvePublicationBooking(
  publicationBooking: UpdateParams<PublicationBookingModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}/approve.json`, options, config)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function unapprovePublicationBooking(
  publicationBooking: UpdateParams<PublicationBookingModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}/unapprove.json`, options, config)
      .then(({ data }: { data: PublicationBookingModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function ftpSync(publicationBooking: UpdateParams<PublicationBookingModel>) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}/ftp_sync.json`, {}, config)
      .then(({ data }: { data: Partial<PublicationBookingModel>}) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function sendUnapprovedArtworkMessage(sendMessagePayload: SendUnapprovedArtworkMessagePayload) {
  const { contacts, message, publicationBookingId: id } = sendMessagePayload

  const config = {
    method: 'POST',
    data: JSON.stringify({
      contacts,
      message,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_bookings/${id}/send_unapproved_artwork_message.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePublicationBooking(publicationBooking: Partial<PublicationBookingModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/publication_bookings/${publicationBooking.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PublicationBookingModuleState = initialState,
  action: Action = { type: '' },
): PublicationBookingModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
